import { create, StoreApi, UseBoundStore } from 'zustand'
import { getResourceObservable, registerResource, registerMultipleResources } from '../storage'
import { getResourceListFetcher } from '../hooks/useResourceList'
import { ListParams, ListProps, Entity, ResourceType } from '../types'

const COLLECTION_STALE_TIME = 3 * 1000

export class Collection<T extends ResourceType> {
  /**
   * A Zustand store hook that can hold the collection state and be added to.
   */
  private readonly usableStore: UseBoundStore<StoreApi<{ ids: string[] | null }>>

  /**
   * Represents a promise that is used for initialization - will be thrown by suspense
   * functions until it is resolved. Only one initialization promise for a Collection is created.
   */
  private initializationPromise?: Promise<any>

  /**
   * After the collection is initialized either a data load or forced hydration this will be true.
   */
  private isInitialized = false

  /**
   * If the collection is hydrated from a cache or otherwise stale this will be true. A stale collection
   * will refresh itself when the STALE_TIME is met.
   */
  private isStale = true

  /**
   * The props if any that are being used to filter this collection.
   */
  private props: ListProps<T>

  constructor(
    private resource: T,
    ...params: ListParams<T>
  ) {
    this.props = params[0]

    this.usableStore = create<{ ids: string[] | null }>((_set) => ({
      ids: null,
    }))

    // if restored state is available, then mark as initialized
    if (this.usableStore.getState().ids) {
      this.isInitialized = true
    }
  }

  public append(item: Entity<T>): void {
    registerResource(item)
    const newIds = new Set([...this.getKeys(), item.id])
    this.usableStore.setState({ ids: Array.from(newIds) })
  }

  public prepend(item: Entity<T>): void {
    registerResource(item)
    const newIds = new Set([item.id, ...this.getKeys()])
    this.usableStore.setState({ ids: Array.from(newIds) })
  }

  public async refresh(): Promise<void> {
    const fetcher = getResourceListFetcher(this.resource)
    const result = await fetcher(this.props)
    this.usableStore.setState({ ids: result.ids })
    this.isInitialized = true
    this.isStale = false
  }

  public hydrate(items: Entity<T>[]): void {
    registerMultipleResources(items)
    this.usableStore.setState({ ids: items.map((i) => i.id) })
    this.isInitialized = true
  }

  public hydrateWithFreshData(items: Entity<T>[]): void {
    this.hydrate(items)
    this.isStale = false
  }

  public getKeys(): string[] {
    return this.usableStore.getState().ids || []
  }

  public getKeysOrNull(): string[] | null {
    return this.usableStore.getState().ids
  }

  /**
   * @todo this is likely to be very slow, we need a better / faster way of getting the simple list
   */
  public getItems(): Entity<T>[] {
    return this.getKeys()
      .map((id) => getResourceObservable(id)?.getValue())
      .filter((v) => !!v)
  }

  public useKeys(): string[] {
    this.initializeIfNeeded()
    this.refreshIfStale()
    return this.usableStore(({ ids }) => ids) as string[]
  }

  public useKeysOrNull(): string[] | null {
    return this.usableStore(({ ids }) => ids)
  }

  public initializeIfNeeded() {
    if (!this.isInitialized) {
      if (!this.initializationPromise) {
        this.initializationPromise = this.refresh()
      }
      throw this.initializationPromise
    }
  }

  private staleTimeout?: NodeJS.Timeout

  public refreshIfStale() {
    if (this.isInitialized && this.isStale && !this.staleTimeout) {
      this.staleTimeout = setTimeout(() => {
        // if still stale after timeout, refresh in the background
        if (this.isStale) {
          this.refresh().then(() => {
            this.staleTimeout = undefined
          })
        }
      }, COLLECTION_STALE_TIME)
    }
  }
}
